import { useState } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';
import { getApiErrorMessage } from '../../../utils/authErrors';

export interface DeleteHourlyDataInput {
  yearMonth: string;
}

export interface DeleteHourlyDataResult {
  success: boolean;
  message?: string;
  deletedCount?: number;
}

export interface UseDeleteHourlyDataReturn {
  deleteHourlyData: (input: DeleteHourlyDataInput) => Promise<DeleteHourlyDataResult>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

export function useDeleteHourlyData(): UseDeleteHourlyDataReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteHourlyData = async (input: DeleteHourlyDataInput): Promise<DeleteHourlyDataResult> => {
    setLoading(true);
    setError(null);

    try {
      const client = getAmplifyClient();
      const inputJson = JSON.stringify(input);

      const result = await client.mutations.deleteHourlyData({
        input: inputJson,
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data) {
        throw new Error('No data returned from deleteHourlyData');
      }

      // Parse the AWSJSON response with type guard
      // AppSync may return already-parsed object or stringified JSON
      let parsedResult: any;

      if (typeof result.data === 'string') {
        // Case 1: String response - parse it
        try {
          parsedResult = JSON.parse(result.data);

          // Case 1a: Double-stringified - parse again
          if (typeof parsedResult === 'string') {
            parsedResult = JSON.parse(parsedResult);
          }
        } catch (parseError) {
          throw new Error(
            `Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`
          );
        }
      } else if (typeof result.data === 'object' && result.data !== null) {
        // Case 2: Already parsed object - use directly
        parsedResult = result.data;
      } else {
        // Case 3: Unexpected type
        throw new Error(
          `Unexpected response type: ${typeof result.data}. Expected string or object.`
        );
      }

      const typedResult: DeleteHourlyDataResult = parsedResult;

      if (!typedResult.success) {
        throw new Error(typedResult.message || 'Deletion failed');
      }

      return typedResult;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setError(null);
  };

  return {
    deleteHourlyData,
    loading,
    error,
    reset,
  };
}
